import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import {
  canCreateAndEditDraftExternalOrder,
  canDeleteDraftExternalOrder,
  canValidateDraftExternalOrder,
} from '@selectors/actions/externalOrderActions';
import { getAuthorizedActions } from '@selectors/featureProps';

import { externalOrder as externalOrderService } from '@services/externalOrder';

import { DUPLICATED_ORDER_STATE_KEY, EXTERNAL_ORDERS_PATH } from '../constants';
import { getOrdersListReturnLocation } from '../utils/ordersListReturnLocation';
import ExternalOrderForm from '../components/ExternalOrderForm';

/**
 * Creation of an external order (/external/orders/create). The order only exists server-side once
 * saved, either as a draft or straight as `A venir` when the draft is validated right away; both
 * paths lead back to the orders list with a toaster.
 *
 * A duplication (COR-1691) lands here as well: the prefilled payload of its source travels in the
 * router state and opens the form filled in, the duplicated order being created on save exactly
 * like a blank one.
 */
export const ExternalOrderCreation = (props) => {
  const {
    history,
    location,
    canCreateAndEditDraft,
    canValidateDraft,
    canDeleteDraft,
    displaySuccessMessage,
    displayErrorMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const [isSaving, setIsSaving] = useState(false);

  const duplicatedExternalOrder = location?.state?.[DUPLICATED_ORDER_STATE_KEY] ?? null;

  // Back to the list as it was left — its tab, page and sort included.
  const goToList = () => {
    history.push(getOrdersListReturnLocation(location));
  };

  const createExternalOrder = async (payload, successMessage, errorMessage) => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    pageLoading();

    try {
      await externalOrderService.createExternalOrder(payload);

      displaySuccessMessage(successMessage);
      goToList();
    } catch {
      displayErrorMessage(errorMessage);
    } finally {
      setIsSaving(false);
      pageLoaded();
    }
  };

  const handleSave = (payload) => {
    createExternalOrder(
      payload,
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_ERROR'),
    );
  };

  const handleValidateDraft = (payload) => {
    createExternalOrder(
      payload,
      i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_ERROR'),
    );
  };

  return (
    <ExternalOrderForm
      canCreateAndEditDraft={canCreateAndEditDraft}
      canDeleteDraft={canDeleteDraft}
      canValidateDraft={canValidateDraft}
      externalOrder={duplicatedExternalOrder}
      isSaving={isSaving}
      isCreation
      onClose={goToList}
      onDelete={goToList}
      onSave={handleSave}
      onValidateDraft={handleValidateDraft}
    />
  );
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_ORDERS_PATH,
  );

  const isDeepsight = state.baseReducer.user.isDeepsight;

  return {
    canCreateAndEditDraft: isDeepsight || canCreateAndEditDraftExternalOrder(authorizedActions),
    canValidateDraft: isDeepsight || canValidateDraftExternalOrder(authorizedActions),
    canDeleteDraft: isDeepsight || canDeleteDraftExternalOrder(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  displaySuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalOrderCreation);
